/**
 * 題目模式的共用邏輯 — 見 mangachat-studio-api `docs/plans/MediaLibrary.spec.md`
 * §3.2（分類）、§4.2（resources 形狀）、§7.2（編輯器要怎麼分岔）。
 *
 * ⚠ **這裡的規則是後端規則的鏡像，後端才是權威**：
 * `QuestModeSettingsValidator.minFrameCountForMode` 與
 * `QuestModeSettingsService.buildResourcesPatch`。
 * 兩邊不同步的後果是「前端讓你存、後端回 400」，所以改動要成對做。
 */

import { attrsOf, wordsOf, isBlocked } from '@/components/console/diary/media/mediaConstants';

export const MODE_WRITING = 'writing';
export const MODE_VIDEO = 'video';
export const MODE_PDF = 'pdf';
export const MODE_GAME_WORDSEARCH = 'game_wordsearch';
export const MODE_GAME_FLIPCARD = 'game_flipcard';
export const MODE_GAME_HOTSPOT = 'game_hotspot';
export const MODE_GAME_COLORWORD = 'game_colorword';

/** 兩層對話框的分類，與規格 §3.2 的表格逐字對應。順序就是顯示順序。 */
export const MODE_GROUPS = [
    { key: 'diary', modes: [MODE_WRITING] },
    { key: 'media', modes: [MODE_VIDEO, MODE_PDF] },
    {
        key: 'game',
        modes: [MODE_GAME_WORDSEARCH, MODE_GAME_FLIPCARD, MODE_GAME_HOTSPOT, MODE_GAME_COLORWORD],
    },
];

export const ALL_MODES = MODE_GROUPS.reduce((acc, g) => acc.concat(g.modes), []);

/** 上限，與後端 `QuestModeSettingsValidator` 一致。 */
/**
 * 翻卡牌的卡片數上限。**5，2026-09-04 從 20 降下來**（與後端
 * `QuestModeSettingsValidator.MAX_CARDS` 同步——改一邊要改兩邊）。
 *
 * ⚠ 20 沒有產品依據，而既有的 10 題翻卡牌**全部只有 1 張卡**。學生端的
 * `FlipCard.vue` 支援多張（一次顯示一張、可往下翻），但**進度指示被註解掉了**、
 * 也沒有進度保存，所以張數多了學生會迷路。要調回去先解開那行進度指示。
 */
export const MAX_CARDS = 5;
/**
 * 找不同的關卡數上限。**1，2026-09-04 從 10 降下來。**
 *
 * ⚠⚠ **這不只是產品簡化，是修正：學生端只會玩第一關。**
 * `FindDifference.vue` 的 `currentIdx` 被設成 0 之後**從來沒有被改過**，
 * 也沒有 `nextLevel`——`winGame()` 直接 `emit('complete')`。
 * （對照 `FlipCard.vue` 有 `currentIndex.value++`，找不同一個都沒有。）
 *
 * 所以老師設 3 關時，學生玩完第 1 關就結束，**2、3 關靜默被丟掉**——不報錯、
 * 沒有任何提示，老師的工作就這樣消失。上限 10 是照「資料結構是陣列」設的，
 * 而那個陣列學生端只讀 `[0]`。
 *
 * ⚠ **要調回去的話，先讓學生端能翻關**（`FindDifference.vue` 需要一個
 * `nextLevel` 與進度指示——`FlipCard` 的進度指示也還是被註解掉的）。
 *
 * ⚠ 與後端 `QuestModeSettingsValidator.MAX_LEVELS` 同步——改一邊要改兩邊。
 */
export const MAX_LEVELS = 1;
export const MAX_TITLE_LENGTH = 100;
export const MIN_GRID_SIZE = 5;
export const MAX_GRID_SIZE = 15;
export const MIN_TIMER = 1;
export const MAX_TIMER = 600;
/** 沒有 timer 的舊資料要補的預設值，與後端 `buildInitialResources` 一致。 */
export const DEFAULT_TIMER = 10;
export const DEFAULT_GRID_SIZE = 11;
export const DEFAULT_TARGET_COUNT = 3;

/**
 * 要不要顯示畫格工作區（規格 §7.2 的表）。
 *
 * ⚠ **`game_wordsearch` 一律顯示**——它是唯一「遊戲 + 寫日記」的組合，
 * 學生玩完之後會進日記編輯區（`PostEdit.vue:284-287` 對 `game.type == 1` 無條件推
 * `'writing'`）。
 *
 * ⚠ **「顯示」與「要求至少一格」是兩件事**，見 minFrameCount。
 */
/**
 * 日記畫格是不是**學生看完影片後由系統合成的**（例如 stage quest 2286）：互動小題同時有
 * `ev` + `emo` + `bh`，或有 `rp`。鏡像後端 `VideoQuest.composesFrameFromTypes`，
 * 規則來自學生端 `DiaryVideo.getSelectedOptions`。
 *
 * ⚠ 這種題目的第一個畫格是**容器**：系統把合成的文字與圖寫進去。老師不能新增或編輯畫格
 * （標題只給資料庫看，學生端不顯示），缺畫格時只警告不擋存檔（2026-11-02 需求方決定）。
 * @param {string[]} types 互動小題的種類（完整的樹或列表摘要的 `video_quest.types`）
 * @returns {boolean}
 */
export function composesFrameFromTypes(types) {
    const list = types || [];
    return (list.includes('ev') && list.includes('emo') && list.includes('bh')) || list.includes('rp');
}

/**
 * 合成日記要用到、所以**不能刪**的小題種類。學生端依 type 找題目
 * （`DiaryVideo.checkVideoDiary`），少一題就合成不出日記；其他小題照常可刪。
 * @param {string[]} types 互動小題的種類
 * @returns {string[]} 不合成時回傳空陣列
 */
export function composeLockedTypes(types) {
    const list = types || [];
    if (list.includes('ev') && list.includes('emo') && list.includes('bh')) {
        return ['ev', 'emo', 'bh'];
    }
    return list.includes('rp') ? ['rp'] : [];
}

/**
 * 列表上要顯示成哪一種模式（只影響文案）。有 `mode` 就是它；沒有 mode 但
 * `is_video_only` 的舊影片題（例如 stage quest 2283）當成影片題，否則 0 格時會顯示
 * 「還沒有畫格」，讓老師去找一個不存在的問題。⚠ 不寫回資料：舊題目照樣沒有 mode。
 * @param {{mode: string|null, is_video_only?: boolean}} quest
 * @returns {string|null}
 */
export function displayMode(quest) {
    if (quest.mode) {
        return quest.mode;
    }
    return quest.is_video_only === true ? MODE_VIDEO : null;
}

export function showsFrameWork(mode, requireWriting) {
    switch (mode) {
        case MODE_WRITING:
        case MODE_GAME_WORDSEARCH:
            return true;
        case MODE_VIDEO:
        case MODE_PDF:
            return requireWriting !== false;
        case MODE_GAME_FLIPCARD:
        case MODE_GAME_HOTSPOT:
        case MODE_GAME_COLORWORD:
            return false;
        default:
            /// ⚠ 沒有 mode 的舊題目（B7 之前）：呼叫端傳 `!is_video_only`。只看影片的
            /// 舊影片題（例如 stage quest 2283）隱藏畫格區——存檔不會改 `is_video_only`，
            /// 老師加了畫格學生也看不到。其餘舊題目照舊顯示。
            if (mode == null) {
                return requireWriting !== false;
            }
            /// 不認識的 mode——寧可多顯示也不要讓編輯器變空白。
            return true;
    }
}

/**
 * 畫格數的**下限**。鏡像後端的 `minFrameCountForMode`。
 *
 * ⚠ **這段說明在 2026-09-01 之前是錯的**，寫著「找單字是 0，因為學生端會自己補一個
 * 虛擬畫格」。那句話只對了一半：確實不會報錯，但虛擬畫格**沒有 `id`**，所以送出的
 * `sample_ids` 是空字串、後端寫進 `null`，那篇日記對應不到任何 `diary_content_sample`
 * （情緒分析的回饋也會被跳過）。下限因此改回 1，見下方 `MODE_GAME_WORDSEARCH` 的註解。
 */
export function minFrameCount(mode, requireWriting) {
    switch (mode) {
        case MODE_WRITING:
            return 1;
        /**
         * ⚠ **找單字是 1，不是 0**（2026-09-01 改回來）。學生玩完之後學生端會把結果
         * 記錄成一篇日記，而那需要一個**真的**畫格：虛擬畫格沒有 `id`，
         * 送出的 `sample_ids` 是空字串，後端寫進 `null`，日記就對應不到任何畫格
         * （情緒分析的回饋也會被跳過）。
         * 建立題目時後端會自動補一個，這個下限是防止老師把它刪掉。
         *
         * ⚠ 與 MODE_WRITING 各自 `return` 而不是共用一個——中間夾了註解，
         * ESLint 的 `no-fallthrough` 認不出兩個 case 是連續的。
         */
        case MODE_GAME_WORDSEARCH:
            return 1;
        case MODE_VIDEO:
        case MODE_PDF:
            return requireWriting !== false ? 1 : 0;
        case MODE_GAME_FLIPCARD:
        case MODE_GAME_HOTSPOT:
        case MODE_GAME_COLORWORD:
            return 0;
        default:
            /// 沒有 mode 的舊題目：只看影片（`is_video_only`）的是 0，其餘維持 1。
            /// 鏡像後端的 `legacyMinFrameCount`。
            if (mode == null) {
                return requireWriting !== false ? 1 : 0;
            }
            return 1;
    }
}

/**
 * 這個模式的畫格是**系統自動建的**，而不是老師編出來的。
 *
 * ⚠ 目前只有找單字：建立題目時後端補一格（`createWordsearchFrameIfNeeded`），
 * 那是「學生玩完自動存下來的那一篇紀錄」而不是老師編的分鏡。
 *
 * ⚠ **這與 `minFrameCount` 是兩件不同的事。** 下限講「不能少於幾格」（找單字是 1，
 * 防止老師把自動建的那一格刪掉）；這一支講「那個數字對老師有沒有意義」。
 * 找單字兩者都成立：下限 1，但「1 個畫格」對老師是雜訊——他沒有編那一格。
 *
 * 用途：題目列表的描述文字。自動建的就講**模式名稱**（「找單字」），
 * 不講畫格數——見 `UnitEdit.questMeta`。
 */
export function framesAreAuto(mode) {
    return mode === MODE_GAME_WORDSEARCH;
}

/** 這個模式有沒有專屬設定區（規格 §7.2）。`writing` 沒有。 */
export function hasModeSettings(mode) {
    return !!mode && mode !== MODE_WRITING && ALL_MODES.indexOf(mode) !== -1;
}

/** 這個模式的「看完要不要寫」開關是否適用。只有影片與 PDF 有。 */
export function hasWritingToggle(mode) {
    return mode === MODE_VIDEO || mode === MODE_PDF;
}

/** 模式 → 這個模式要挑的素材 `purpose`。⚠ 翻卡牌與找不同是列表，不在這裡。 */
export function purposeOfMode(mode) {
    switch (mode) {
        case MODE_VIDEO: return 'video';
        case MODE_PDF: return 'pdf';
        case MODE_GAME_WORDSEARCH: return 'wordsearch';
        case MODE_GAME_COLORWORD: return 'colorword';
        default: return null;
    }
}

/**
 * 把後端的 `mode_settings` 正規化成編輯器要用的形狀。
 *
 * ⚠ **缺的欄位要補預設值而不是留 undefined**：`v-model` 綁到 undefined 的欄位，
 * 使用者一動就變成「有值」，dirty 比對會誤判。而 `1698` 那種沒有 timer 的舊資料
 * （轉換計畫 §3.7）就是靠這裡補上 DEFAULT_TIMER。
 */
export function normalizeSettings(mode, raw) {
    const src = raw && typeof raw === 'object' ? raw : {};
    const game = src.game && typeof src.game === 'object' ? src.game : {};
    return {
        video_media_id: toId(src.video_media_id),
        pdf_media_id: toId(src.pdf_media_id),
        /// 沒送就是 true（後端的預設也是 true，規格 §4.1）
        require_writing: src.require_writing !== false,
        game: {
            wordlist_media_id: toId(game.wordlist_media_id),
            grid_size: toInt(game.grid_size, DEFAULT_GRID_SIZE),
            target_count: toInt(game.target_count, DEFAULT_TARGET_COUNT),
            is_cheat_mode: game.is_cheat_mode === true,
            timer: toInt(game.timer, DEFAULT_TIMER),
            cards: Array.isArray(game.cards) ? game.cards.map(normalizeCard) : [],
            levels: Array.isArray(game.levels) ? game.levels.map(normalizeLevel) : [],
        },
    };
}

function normalizeCard(card) {
    const c = card && typeof card === 'object' ? card : {};
    return {
        front_media_id: toId(c.front_media_id),
        front_title: typeof c.front_title === 'string' ? c.front_title : '',
        back_media_id: toId(c.back_media_id),
        back_title: typeof c.back_title === 'string' ? c.back_title : '',
    };
}

function normalizeLevel(level) {
    const l = level && typeof level === 'object' ? level : {};
    return {
        hotspot_media_id: toId(l.hotspot_media_id),
        title: typeof l.title === 'string' ? l.title : '',
    };
}

/**
 * 只取這個模式**擁有**的 key 送給後端（規格 §4.2）。
 *
 * ⚠ **不要整包送。** 後端會忽略不屬於該模式的 key，所以送了不會出錯——但它們會進
 * dirty 比對的 baseline，於是「換一下模式無關的欄位」也會讓題目看起來被改過。
 *
 * ⚠ **`mode` 不在裡面。** 後端收到與現況不同的 mode 會回 400（不可改，ADR 0002），
 * 送一樣的值只是給「哪裡能改 mode」多開一個入口。
 */
export function toPayload(mode, s) {
    switch (mode) {
        case MODE_WRITING:
            return {};
        case MODE_VIDEO:
            return { video_media_id: s.video_media_id, require_writing: s.require_writing };
        case MODE_PDF:
            return { pdf_media_id: s.pdf_media_id, require_writing: s.require_writing };
        case MODE_GAME_WORDSEARCH:
            return {
                game: {
                    wordlist_media_id: s.game.wordlist_media_id,
                    grid_size: s.game.grid_size,
                    target_count: s.game.target_count,
                    is_cheat_mode: s.game.is_cheat_mode,
                },
            };
        case MODE_GAME_FLIPCARD:
            return { game: { cards: s.game.cards } };
        case MODE_GAME_HOTSPOT:
            return { game: { levels: s.game.levels } };
        case MODE_GAME_COLORWORD:
            return {
                game: { wordlist_media_id: s.game.wordlist_media_id, timer: s.game.timer },
            };
        default:
            /// 沒有 mode 的舊題目：什麼都不送。送了後端也會整段跳過（規格 §9），
            /// 但它會污染 dirty baseline，讓舊題目一開啟就顯示「有未儲存的變更」。
            return {};
    }
}

/// ⚠ `attrsOf` 與 `wordsOf` 定義在 media/mediaConstants.js——那是較低層的模組，
/// 「我的素材」頁、挑選器與字表編輯器也要用它們。這裡 import 後再轉出去
/// （下面的 `targetsOf` 與 `validateSettings` 要用到，光 `export ... from` 不會把
/// 名稱帶進這個模組的作用域——那個坑踩過一次），**不要在這裡再寫一份**。
/// 兩份 `wordsOf` 的後果是靜默的：其中一份漏掉 LONGTEXT 回字串的容錯，字表就變空的。
export { attrsOf, wordsOf };


export function targetsOf(media) {
    const t = attrsOf(media).targets;
    return Array.isArray(t) ? t : [];
}

function toId(value) {
    const n = Number(value);
    return Number.isInteger(n) && n > 0 ? n : null;
}

function toInt(value, fallback) {
    const n = Number(value);
    return Number.isInteger(n) ? n : fallback;
}

/**
 * 模式設定的驗證，**鏡像後端規格 §8**。回傳 `{ level, key, params }` 陣列，
 * `key` 是 i18n 的後綴（`unit.settings.err_*`），由呼叫端翻譯。
 *
 * ⚠ **只做前端算得出來的部分。** 素材的 `purpose` 是否相符、找不同圖有沒有熱區、
 * 影片轉檔完了沒——這些後端一定會再驗一次（B8），因為端點可以直接打。
 * 這裡的目的是「不要讓老師按下存檔才知道」，不是取代後端。
 *
 * ⚠ `level: 'block'` 會擋存檔，`'warn'` 只提示。判準是「後端會不會回 400」。
 */
export function validateSettings(mode, s, mediaMap) {
    const rows = [];
    const media = (id) => (id ? mediaMap[String(id)] || null : null);
    const need = (id, key) => {
        if (!id) rows.push({ level: 'block', key });
    };
    /// 不能用的素材：後端 B8 會擋（規格 §8 要求 status = active），所以是 block。
    /// ⚠ 走 `isBlocked` 而不是比對 `delisted`：平台下架（`banned`）也不能用，
    /// 而那種情況老師**還不能自己上架**，更需要在這裡就擋住並叫他換一個。
    const checkDelisted = (id, key) => {
        if (isBlocked(media(id))) rows.push({ level: 'block', key });
    };
    /**
     * ⚠ **只有 `failed` 擋存檔**（2026-08-31 放寬，與後端一致）。
     *
     * `pending` / `processing` 只提示不擋：自行上傳影片時前端會自動把它套用到欄位上，
     * 若同時擋住存檔就變成死路——老師沒有任何方式知道何時能存，因為素材狀態是
     * 載入時的快照、不會自己更新，而轉檔要 30 秒–3 分鐘再加 cron 間隔。
     *
     * ⚠ `failed` 不一樣：那支影片**永遠不會**好，存進去是永久壞掉的引用，
     * 而且沒有機制會回頭修它。「還在處理」與「再也不會好」要分開。
     */
    const checkProcessing = (id, failedKey) => {
        const m = media(id);
        if (m && m.process_status === 'failed') {
            rows.push({ level: 'block', key: failedKey });
        }
    };

    switch (mode) {
        case MODE_VIDEO:
            need(s.video_media_id, 'err_video_required');
            checkDelisted(s.video_media_id, 'err_video_delisted');
            checkProcessing(s.video_media_id, 'err_video_failed');
            break;

        case MODE_PDF:
            need(s.pdf_media_id, 'err_pdf_required');
            checkDelisted(s.pdf_media_id, 'err_pdf_delisted');
            break;

        case MODE_GAME_WORDSEARCH: {
            need(s.game.wordlist_media_id, 'err_wordlist_required');
            checkDelisted(s.game.wordlist_media_id, 'err_wordlist_delisted');
            const grid = s.game.grid_size;
            if (!Number.isInteger(grid) || grid < MIN_GRID_SIZE || grid > MAX_GRID_SIZE) {
                rows.push({
                    level: 'block',
                    key: 'err_grid_range',
                    params: { min: MIN_GRID_SIZE, max: MAX_GRID_SIZE },
                });
            }
            const count = s.game.target_count;
            if (!Number.isInteger(count) || count < 1) {
                rows.push({ level: 'block', key: 'err_target_min' });
            } else {
                /// ⚠ 上界是字表的字數（規格 §8）。⚠ **只有在字表載得到時才檢查**——
                /// 素材還沒選、或 mediaMap 裡沒有那一筆時，這裡不能誤報，
                /// 否則老師會看到一個他無法理解的錯誤。後端一定會再驗一次。
                const words = wordsOf(media(s.game.wordlist_media_id)).length;
                if (words > 0 && count > words) {
                    rows.push({ level: 'block', key: 'err_target_max', params: { n: words } });
                }
            }
            /**
             * 每一個字都要放得進這一題的格子（2026-09-04 補上，後端 9/1 就有了）。
             *
             * ⚠ **漏了這一條的症狀是「檢查清單全綠卻存不了」**：後端的
             * `WordlistValidator.assertWordsFitGrid` 會擋，前端沒擋 → 儲存按鈕是
             * enabled → 按下去拿到 400。
             *
             * ⚠ 這一項**只能在這裡檢查**：字長是素材屬性、`grid_size` 是題目設定，
             * 同一份字表在 `grid_size = 15` 合法而在 11 不合法。建立字表時只擋得掉
             * 「> 15，任何難度都放不進去」的字。
             *
             * ⚠ 沒擋住的話學生會遇到**靜默的死路**：`generateGame()` 放不進去就試 100 次
             * 然後放棄且不報錯，那個字根本不在格子裡。
             *
             * ⚠ 同 `err_target_max`：只在字表載得到時檢查，否則會對「還沒選素材」誤報。
             */
            if (Number.isInteger(grid)) {
                const longest = wordsOf(media(s.game.wordlist_media_id))
                    .reduce((a, w) => (w.length > a.length ? w : a), '');
                if (longest.length > grid) {
                    rows.push({
                        level: 'block',
                        key: 'err_word_too_long_for_grid',
                        params: { w: longest.toUpperCase(), len: longest.length, grid: grid },
                    });
                }
            }
            break;
        }

        case MODE_GAME_COLORWORD: {
            need(s.game.wordlist_media_id, 'err_wordlist_required');
            checkDelisted(s.game.wordlist_media_id, 'err_wordlist_delisted');
            const t = s.game.timer;
            if (!Number.isInteger(t) || t < MIN_TIMER || t > MAX_TIMER) {
                rows.push({
                    level: 'block',
                    key: 'err_timer_range',
                    params: { min: MIN_TIMER, max: MAX_TIMER },
                });
            }
            break;
        }

        case MODE_GAME_FLIPCARD:
            if (s.game.cards.length === 0) {
                rows.push({ level: 'block', key: 'err_cards_required' });
            }
            s.game.cards.forEach((c, i) => {
                /// ⚠ **兩面都要有圖**（規格 §8）。少一面學生會翻到空白。
                if (!c.front_media_id || !c.back_media_id) {
                    rows.push({ level: 'block', key: 'err_card_incomplete', params: { n: i + 1 } });
                }
                if (isBlocked(media(c.front_media_id))
                    || isBlocked(media(c.back_media_id))) {
                    rows.push({ level: 'block', key: 'err_card_delisted', params: { n: i + 1 } });
                }
                /// 標題空著只是還沒填完，不擋
                if (!c.front_title.trim() && !c.back_title.trim()) {
                    rows.push({ level: 'warn', key: 'err_card_no_title', params: { n: i + 1 } });
                }
            });
            break;

        case MODE_GAME_HOTSPOT:
            /// ⚠ 訊息講「尚未選擇素材」而不是「至少要有一關」：上限降到 1 之後 UI 就
            /// 沒有「新增一關」了（沒有關卡時直接給一個空的素材欄），所以叫老師「加一關」
            /// 是叫他做一件介面上不存在的動作（2026-09-09 修）。
            if (s.game.levels.length === 0) {
                rows.push({ level: 'block', key: 'err_hotspot_no_media' });
            }
            /**
             * ⚠ **超過上限要擋在這裡**（2026-09-04，上限從 10 降到 1）。
             * 後端 `parseQuestModeSettings` 會回 400，前端沒擋的話就是「清單乾淨
             * 卻存不了」。
             *
             * ⚠ 會走到這裡的只有**降上限之前建的多關題目**。UI 已經不讓新增第 2 關，
             * 所以正常操作產不出來。訊息要告訴老師「移除多的那幾關」，
             * 否則他會卡在一個看不懂的錯誤上。
             */
            if (s.game.levels.length > MAX_LEVELS) {
                rows.push({
                    level: 'block',
                    key: 'err_levels_too_many',
                    params: { n: s.game.levels.length, max: MAX_LEVELS },
                });
            }
            s.game.levels.forEach((l, i) => {
                /// ⚠ 只有一關時訊息不帶「第 N 關」：版面上也刻意不顯示關卡編號
                /// （見 QuestModeSettings 的 v-for），提示裡冒出「第 1 關」對不上畫面。
                /// 多關的舊題目才需要編號指出是哪一關。
                /// ⚠ 宣告在回呼裡而不是 case 區塊裡：`no-case-declarations` 不允許
                /// 在 case 底下直接宣告（要嘛包一層大括號，要嘛放進函式主體）。
                const oneLevel = s.game.levels.length <= 1;
                if (!l.hotspot_media_id) {
                    rows.push(oneLevel
                        ? { level: 'block', key: 'err_hotspot_no_media' }
                        : { level: 'block', key: 'err_level_no_media', params: { n: i + 1 } });
                    return;
                }
                const m = media(l.hotspot_media_id);
                /// 同上：只有一關就不帶「第 N 關」。`row()` 讓四條訊息用同一套規則，
                /// 不然改一條漏三條。
                const row = (base) => (oneLevel
                    ? { level: 'block', key: `err_hotspot_${base}` }
                    : { level: 'block', key: `err_level_${base}`, params: { n: i + 1 } });
                if (isBlocked(m)) {
                    rows.push(row('delisted'));
                }
                /// ⚠ 這兩條是規格 §8 點名「必須擋、因為老師看不出原因」的：
                /// 沒有熱區 → 遊戲永遠無法完成（學生卡死，不報錯）；
                /// 沒有寬高 → 學生端元件直接 crash（白畫面）。
                /// 後端一定會擋，但讓老師在選的當下就知道比按存檔才知道好得多。
                if (m && targetsOf(m).length === 0) {
                    rows.push(row('no_targets'));
                }
                if (m && (m.width === null || m.height === null)) {
                    rows.push(row('no_size'));
                }
            });
            break;

        default:
            break;
    }
    return rows;
}
